import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ONBOARDING_PRODUCTS } from "../../../constants";
import Select from "../../../parts/inputs/select";
import SvgIcon from "../../../parts/SvgIcon";
import ToolTip from "../../../parts/tooltip";
import CollectRepMaxModal from "../../dashboard/settings/CollectRepMaxModal";
import StepHeading from "./StepHeading";

const getDayOptions = (t) => [
  { text: t("signupCoach.daySunday"), value: 0 },
  { text: t("signupCoach.dayMonday"), value: 1 },
  { text: t("signupCoach.dayTuesday"), value: 2 },
  { text: t("signupCoach.dayWednesday"), value: 3 },
  { text: t("signupCoach.dayThursday"), value: 4 },
  { text: t("signupCoach.dayFriday"), value: 5 },
  { text: t("signupCoach.daySaturday"), value: 6 },
];

const LANGUAGE_OPTIONS = [
  { text: "English", value: "en" },
  { text: "Español", value: "es" },
];

const PrefRow = ({ label, tooltipId, tooltip, children }) => {
  const { t } = useTranslation("dashboard");
  return (
    <div className="pref-row">
      <div className="pref-label">
        {label}
        {tooltip && (
          <ToolTip id={tooltipId} body={tooltip}>
            <SvgIcon
              alt={t("generalPreferences.infoIconAlt")}
              className="cursor-pointer"
              src="/img/new-icons/question-circle.svg"
            />
          </ToolTip>
        )}
      </div>
      {children}
    </div>
  );
};

// Two-option pill toggle that submits its value through a hidden field, so the
// wizard doesn't need state for settings nothing else on the page reads.
const PrefToggle = ({ name, options, defaultValue }) => {
  const [value, setValue] = useState(defaultValue);
  return (
    <div className="unit-toggle">
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={value === option.value ? "active" : ""}
          onClick={() => setValue(option.value)}
        >
          {option.label}
        </button>
      ))}
      <input type="hidden" name={name} value={String(value)} />
    </div>
  );
};

// Step 6 — general and training app preferences (a stopgap copy of Settings >
// Preferences until the Setup Wizard covers them).
const StepPreferences = ({
  current,
  state,
  onLanguageChange,
  onUnitsChange,
  onDayChange,
}) => {
  const { t } = useTranslation("auth");
  const { t: td } = useTranslation("dashboard");

  const onOff = [
    { label: td("generalPreferences.on"), value: true },
    { label: td("generalPreferences.off"), value: false },
  ];
  const showHide = [
    { label: td("generalPreferences.show"), value: false },
    { label: td("generalPreferences.hide"), value: true },
  ];

  return (
    <div className="step-body step-preferences">
      <StepHeading
        current={current}
        title={t("onboardingWizard.preferences.title")}
        subtitle={t("onboardingWizard.preferences.subtitle")}
      />

      <div className="prefs-card">
        <PrefRow label={t("onboardingWizard.preferences.appLanguage")}>
          <Select
            name="language"
            options={LANGUAGE_OPTIONS}
            value={state.language}
            onChange={onLanguageChange}
            overlay
          />
        </PrefRow>

        <PrefRow label={t("onboardingWizard.preferences.measurement")}>
          <div className="unit-toggle">
            <button
              type="button"
              className={state.units === "metric" ? "active" : ""}
              onClick={() => onUnitsChange("metric")}
            >
              {t("signupCoach.metricKg")}
            </button>
            <button
              type="button"
              className={state.units === "imperial" ? "active" : ""}
              onClick={() => onUnitsChange("imperial")}
            >
              {t("signupCoach.imperialLbs")}
            </button>
          </div>
        </PrefRow>

        <PrefRow label={t("onboardingWizard.preferences.firstDay")}>
          <Select
            name="startingCalendarDay"
            options={getDayOptions(t)}
            value={state.startingCalendarDay}
            onChange={onDayChange}
            overlay
          />
        </PrefRow>

        {state.products.includes(ONBOARDING_PRODUCTS.PULL) && (
          <PrefRow label={td("pullPreferences.userReference")}>
            <PrefToggle
              name="referAsClients"
              defaultValue={false}
              options={[
                { label: td("pullPreferences.athlete"), value: false },
                { label: td("pullPreferences.client"), value: true },
              ]}
            />
          </PrefRow>
        )}

        <PrefRow
          label={td("generalPreferences.roundingToGymIncrements")}
          tooltipId="wizard-rounding-tooltip"
          tooltip={td("generalPreferences.roundingTooltip")}
        >
          <PrefToggle name="round" defaultValue={true} options={onOff} />
        </PrefRow>

        <PrefRow label={td("generalPreferences.collectRepMaxOnboarding")}>
          <CollectRepMaxModal userToBindInformation={{}} inForm />
        </PrefRow>

        <PrefRow
          label={td("generalPreferences.strengthTrainingMetricReports")}
          tooltipId="wizard-strength-metrics-tooltip"
          tooltip={td("generalPreferences.strengthTrainingTooltip")}
        >
          <PrefToggle
            name="hideStrengthMetrics"
            defaultValue={false}
            options={showHide}
          />
        </PrefRow>

        <PrefRow
          label={td("generalPreferences.estimatedOneRepMax")}
          tooltipId="wizard-e1rm-tooltip"
          tooltip={td("generalPreferences.oneRepTooltip")}
        >
          <PrefToggle name="hideE1rm" defaultValue={false} options={showHide} />
        </PrefRow>

        <PrefRow
          label={td("generalPreferences.intensityMeasure")}
          tooltipId="wizard-intensity-tooltip"
          tooltip={td("generalPreferences.intensityMeasureTooltip")}
        >
          <PrefToggle
            name="intensityMeasure"
            defaultValue="rpe"
            options={[
              { label: td("generalPreferences.rpe"), value: "rpe" },
              { label: td("generalPreferences.rir"), value: "rir" },
            ]}
          />
        </PrefRow>
      </div>

      <input type="hidden" name="units" value={state.units} />
    </div>
  );
};

export default StepPreferences;
